import JsonInputField from 'elements/field/JsonInputField'
import lodash from 'lodash'
import { InputProps, useInput } from 'ra-core'
import { Labeled } from 'ra-ui-materialui'
import { useMemo, useRef } from 'react'
import { JsonFormsDebuggable, JsonFormsDebuggableProps } from './JsonFormsDebuggable'

type Props = InputProps &
  Omit<JsonFormsDebuggableProps, 'onChange' | 'data' | 'validate' | 'schema'> & {
    fullWidth?: boolean
    stringify?: boolean
    schema?: JsonFormsDebuggableProps['schema']
    rows?: number
    fallbackProps?: any
  }

function isPlainObject(value: any) {
  // For null and undefined.
  if (value == null) {
    return false
  }

  const prototype = Object.getPrototypeOf(value)
  return prototype === null || prototype === Object.prototype
}

const convertObjectEmptyPropertyToUndefine = <T extends any>(val: T, requiredKeys: string[]): T | undefined => {
  if (isPlainObject(val)) {
    const newObject = {}
    for (const [key, value] of Object.entries(val as Record<string, any>)) {
      if (value === '' && requiredKeys.includes(key)) {
        newObject[key] = undefined
      } else {
        newObject[key] = convertObjectEmptyPropertyToUndefine(value, requiredKeys)
      }
    }
    return newObject as T
  } else if (Array.isArray(val)) {
    const newArray = [] as any[]
    for (const item of val) {
      newArray.push(convertObjectEmptyPropertyToUndefine(item, requiredKeys))
    }
    return newArray as T
  } else {
    return val
  }
}
export const JsonFormsInput = (props: Props) => {
  const errors = useRef<any>(null)
  const validate = () => {
    return errors.current
  }
  const { id, input, meta, isRequired } = useInput({ ...props, validate })
  const requiredFields = props.schema?.required ?? []

  const realValue = useMemo(() => {
    if (props.stringify && input.value === null) return null
    return props.stringify ? JSON.parse(input.value) : input.value
  }, [input.value])

  if (!props.schema) {
    return (
      <JsonInputField
        label={props.label}
        source={props.source}
        resource={props.resource}
        stringify={props.stringify}
        fullWidth={props.fullWidth}
        validate={props.validate}
        rows={props.rows}
        {...(props.fallbackProps || {})}
      />
    )
  }

  const onChange = (newData: any, errors: any) => {
    errors.current = errors
    const normalizedNewData = convertObjectEmptyPropertyToUndefine(newData, requiredFields)

    // JSON Forms dispatches onChange whenever realValue is updated, not just when there has been user input
    // It also clones the data object, making it impossible to check by reference
    // Whithout this check, the change handler can be fired recursively, crashing the app
    if (lodash.isEqual(normalizedNewData, realValue)) return

    const newValue = props.stringify ? JSON.stringify(normalizedNewData) : normalizedNewData
    input.onChange(newValue)
  }

  return (
    <Labeled
      id={id}
      label={props.label}
      source={props.source}
      resource={props.resource}
      isRequired={isRequired}
      meta={meta}
      input={input}
      fullWidth={props.fullWidth}
    >
      <JsonFormsDebuggable data={realValue} onChange={onChange} schema={props.schema} uischema={props.uischema} />
    </Labeled>
  )
}
